import { useId, useRef, useState } from 'react';
import type { ReferenceLibraryMeta } from '@scoutaround/script-breakdown-shared';

import { Icon } from '../Icon.js';
import type { AddMode } from './EmptyLibrary.js';
import { acceptAttribute, uploadLimitText } from './format.js';

/**
 * THE QUICK-ADD BAR: the three kinds as one segmented control, a field to paste
 * a link into, and a drop zone. Every one of them opens the ONE add drawer
 * (design §6) — the paste carries its link there, a drop or a pick carries its
 * files — so there is a single place where a reference is described and saved.
 */
export function QuickAddBar({
  upload,
  onOpen,
}: {
  upload: ReferenceLibraryMeta['upload'] | undefined;
  onOpen: (mode: AddMode, seed?: { url?: string; files?: File[] }) => void;
}) {
  const [url, setUrl] = useState('');
  const [over, setOver] = useState(false);
  const picker = useRef<HTMLInputElement>(null);
  const urlId = useId();
  const uploadsOff = upload?.allowed === false;

  return (
    <section className="ref-quick" aria-label="Add a reference" data-testid="ref-quick">
      <div className="ref-quick-modes" role="group" aria-label="What to add">
        <button type="button" className="ref-quick-mode" data-testid="ref-quick-link" onClick={() => { onOpen('LINK'); }}>
          <Icon name="link" size={15} />
          Link
        </button>
        <button type="button" className="ref-quick-mode" data-testid="ref-quick-upload" onClick={() => { onOpen('UPLOAD'); }}>
          <Icon name="upload" size={15} />
          Upload file
        </button>
        <button type="button" className="ref-quick-mode" data-testid="ref-quick-note" onClick={() => { onOpen('NOTE'); }}>
          <Icon name="note" size={15} />
          Note
        </button>
      </div>

      <form
        className="ref-quick-paste"
        onSubmit={(event) => {
          event.preventDefault();
          const value = url.trim();
          if (value === '') return;
          setUrl('');
          onOpen('LINK', { url: value });
        }}
      >
        <label className="sr-only" htmlFor={urlId}>
          Paste a link
        </label>
        <input
          id={urlId}
          className="ref-input"
          type="text"
          inputMode="url"
          autoComplete="off"
          value={url}
          placeholder="Paste a link (Vimeo, YouTube, article, image)…"
          data-testid="ref-quick-url"
          onChange={(event) => {
            setUrl(event.target.value);
          }}
        />
        <button type="submit" className="ctl ref-quick-go" disabled={url.trim() === ''} data-testid="ref-quick-url-add">
          Add
        </button>
      </form>

      <span className="ref-quick-or" aria-hidden="true">
        or
      </span>

      <div
        className="ref-drop"
        data-over={over ? '1' : undefined}
        data-testid="ref-quick-drop"
        onDragOver={(event) => {
          event.preventDefault();
          setOver(true);
        }}
        onDragLeave={() => {
          setOver(false);
        }}
        onDrop={(event) => {
          event.preventDefault();
          setOver(false);
          const files = Array.from(event.dataTransfer.files);
          if (files.length > 0) onOpen('UPLOAD', { files });
        }}
      >
        <Icon name="cloudUpload" size={18} />
        <span className="ref-drop-text">
          <span>
            {uploadsOff ? 'Uploading files is not in this plan' : 'Drag & drop files here or '}
            {uploadsOff ? null : (
              <button
                type="button"
                className="ref-drop-browse"
                data-testid="ref-quick-browse"
                onClick={() => {
                  picker.current?.click();
                }}
              >
                browse
              </button>
            )}
          </span>
          {upload && !uploadsOff ? (
            <span className="ref-drop-limit" data-testid="ref-quick-limit">
              {uploadLimitText(upload)}
            </span>
          ) : null}
        </span>
        {upload && !uploadsOff ? (
          <input
            ref={picker}
            type="file"
            multiple
            hidden
            accept={acceptAttribute(upload)}
            data-testid="ref-quick-file-input"
            onChange={(event) => {
              const files = Array.from(event.target.files ?? []);
              event.target.value = '';
              if (files.length > 0) onOpen('UPLOAD', { files });
            }}
          />
        ) : null}
      </div>
    </section>
  );
}
